import { Box } from "@chakra-ui/react";
import type { ITooltipParams } from "ag-grid-community";

// AG Grid's own tooltip renders directly below whatever it's attached to - for a header cell
// that's right on top of the floating-filter search row, obscuring it every time you hover a
// header. Flips the tooltip to render above its anchor instead, when there's room to fit it
// there ("when possible" - falls back to AG Grid's own default placement otherwise).
//
// Deliberately does NOT measure this component's own rendered element: AG Grid's tooltip popup
// wrapper has no real layout (getBoundingClientRect() reads all zeros) at mount time, and still
// doesn't even one requestAnimationFrame later (confirmed empirically) - there's no reliable
// moment to read its position from the inside. The header CELL being hovered, by contrast, is a
// normal, already-laid-out part of the grid's own DOM, so its position is available immediately
// and synchronously - use that instead to decide placement, then render with the right transform
// from the first paint (no flicker, no race).
// Minimum clearance (px) required above the header cell before flipping. Needs to comfortably
// cover the ActionBar (48px height + 8px margin, see layoutConstants.ts) plus the flipped
// tooltip's own rendered height (~24px) plus its 34px gap from the header - too low a threshold
// flips the tooltip into a position that's technically "above the header" but still clipped
// behind/under the ActionBar, rendering invisible (confirmed empirically at a 40px threshold).
const MIN_CLEARANCE_ABOVE_PX = 110;

function hasEnoughRoomAbove(params: ITooltipParams): boolean {
  const column = params.column as { getColId?: () => string } | undefined;
  if (params.location !== "header" || !column?.getColId) return false;
  const colId = column.getColId();
  const headerCell = document.querySelector(`.ag-header-cell[col-id="${CSS.escape(colId)}"]`);
  if (!headerCell) return false;
  return headerCell.getBoundingClientRect().top > MIN_CLEARANCE_ABOVE_PX;
}

export default function GridHeaderTooltip(params: ITooltipParams) {
  const placeAbove = hasEnoughRoomAbove(params);

  return (
    <Box
      bg="gray.700"
      color="white"
      fontSize="xs"
      px={2}
      py={1}
      borderRadius="sm"
      boxShadow="md"
      whiteSpace="nowrap"
      transform={placeAbove ? "translateY(calc(-100% - 34px))" : undefined}
    >
      {params.value}
    </Box>
  );
}
